import {
  RootState,
  DocumentToStoreState,
  DocumentToStoreModel,
  LockModel,
  ErrorModel,
  DocumentToStoreViewModel,
  Dictionary,
  SchemaModel,
  Cabinet,
  ExistingDocumentOption,
  CustomIndexingLookupModel,
  DocumentTypeModel
} from '@/types';
import { ReferenceLookupType, PagesStoreMode } from '@/types/enums';
import { GetterTree } from 'vuex';

export const getters: GetterTree<DocumentToStoreState, RootState> = {
  documentToStore(state): DocumentToStoreModel {
    return state.documentToStore;
  },
  entityLock(state): LockModel | undefined {
    return state.documentToStore.lock;
  },
  extendingLock(state): boolean {
    return state.extendingDocumentToStoreLock;
  },
  componentName(state): string {
    return state.documentToStore.componentName;
  },
  errorModel(state): ErrorModel {
    return state.documentToStore.errorModel;
  },
  isLoading(state): boolean {
    return state.isLoading;
  },
  isReadonly(state): boolean {
    return state.isReadonly;
  },
  documentToStoreView(state): DocumentToStoreViewModel {
    return state.documentToStoreView;
  },
  referenceValues(state): Dictionary<string> {
    const referenceValues = {} as Dictionary<string>;
    state.documentToStoreView.dynamicFormSchema
      .filter((field: SchemaModel) => field.id !== Cabinet.DocumentDateReference.FieldKey)
      .forEach((field: SchemaModel) => {
        referenceValues[field.id] = field.value;
      });

    return referenceValues;
  },
  referenceFields(state: DocumentToStoreState): SchemaModel[] {
    const fields: SchemaModel[] = state.documentToStoreView.dynamicFormSchema.filter((field: SchemaModel) => field.id.toLowerCase() !== Cabinet.DocumentDateReference.FieldKey.toLowerCase());
    return [...fields];
  },
  documentDateReferenceValue(state): Date | null {
    const documentDateSchema = state.documentToStoreView.dynamicFormSchema.find((schema) => schema.id === Cabinet.DocumentDateReference.FieldKey);
    return documentDateSchema ? documentDateSchema.value : null;
  },
  isSpecialLookupField: (state) => (fieldKey: string): boolean => {
    return (
      state.documentToStoreView.targetDocumentTypeSettings.referenceLookupType === ReferenceLookupType.SpecialLookupSql &&
      state.documentToStoreView.targetDocumentTypeSettings.lookupReferencesKeys.includes(fieldKey)
    );
  },
  sourceDocumentTypeId(state): number {
    return state.selectedSourceDocumentTypeId;
  },
  targetCabinetId(state, getters): string {
    const targetCabinet = state.documentToStoreView.cabinets?.find((cabinet) => cabinet.documentTypes.some((type) => type.id === getters.targetDocumentTypeId));

    return targetCabinet ? targetCabinet.id : '';
  },
  targetDocumentTypeId(state, getters, rootState: any, rootGetters): number | null {
    const userTargetDocTypes = rootGetters['userPreferencesStore/getDocumentToStoreTargetDocumentTypes'];

    if (!userTargetDocTypes) {
      return null;
    }

    return userTargetDocTypes[getters.sourceDocumentTypeId];
  },
  getCabinetIdFromDocumentType: (state) => (documentTypeId: number): string | null => {
    const targetCabinet = state.documentToStoreView.cabinets?.find((cabinet) => cabinet.documentTypes.some((type) => type.id === documentTypeId));

    return targetCabinet ? targetCabinet.id : null;
  },
  firstDocumentTypeInCabinet: (state) => (cabinetId: string): number | null => {
    const cabinet = state.documentToStoreView.cabinets?.find((cabinet) => cabinet.id === cabinetId);

    if (!cabinet || cabinet.documentTypes.length === 0) {
      return null;
    }

    const sortedDocumentTypes = cabinet.documentTypes.sort((item1: DocumentTypeModel, item2: DocumentTypeModel) => (item1.description > item2.description ? 1 : -1));

    return sortedDocumentTypes[0].id;
  },
  cabinetDocumentTypesCount: (state) => (cabinetId: string): number | null => {
    const cabinet = state.documentToStoreView.cabinets?.find((cabinet) => cabinet.id === cabinetId);
    return cabinet?.documentTypes ? cabinet.documentTypes?.length : null;
  },
  existingDocumentSet(state): boolean {
    return state.existingDocumentId > 0;
  },
  existingDocumentId(state): number | null {
    return state.existingDocumentId > 0 ? state.existingDocumentId : null;
  },
  existingDocumentOption(state): ExistingDocumentOption {
    return state.existingDocumentOption;
  },
  initialReferenceValues(state): SchemaModel[] {
    return state.initialSchema;
  },
  initialMultiValueReferenceValues(state): any[] {
    return state.initialMultiValueReferences;
  },
  initialDocumentDateValue(state): Date | null {
    return state.initialDocumentDate;
  },
  retainReferences(state): boolean {
    return state.documentToStoreView?.targetDocumentTypeSettings?.retainCommonReferenceValues || state.documentToStoreView?.targetDocumentTypeSettings?.retainDocumentReferenceValues;
  },
  clearGridAfterUpdate(state): boolean {
    return state.documentToStoreView?.targetDocumentTypeSettings?.clearGridAfterUpdate;
  },
  isFocusToDocumentTypeAfterStore(state): boolean {
    return state.documentToStore?.sourceDocumentTypeSettings?.setFocusToDocumentTypeAfterStore ? true : false;
  },
  customIndexingLookups(state, getters): CustomIndexingLookupModel[] | null {
    if (!getters.targetDocumentTypeId) {
      return null;
    }

    return state.documentToStoreView.customIndexingLookups;
  },
  keepPagesAfterStoring(state): boolean {
    return state.keepPagesAfterStoring;
  },
  getSelectedPages(state): string {
    return state.selectedPages;
  },
  clearDocumentDate(state): boolean {
    return state.documentToStoreView?.targetDocumentTypeSettings?.clearDocumentDate;
  },
  documentStatus(state): string {
    return state.documentToStore.status;
  },
  documentStatusMaximumLength(state): number {
    return state.documentToStore.statusMaximumLength;
  },
  pageViewedBeforeAddToExisting(state): number {
    return state.pageViewedBeforeAddToExisting;
  },
  multiValueReferenceValues(state): any[] {
    return state.documentToStoreView.multiValueReferences;
  },
  allPagesSelectedWithManualPageSelection(state): boolean {
    return state.allPagesSelectedWithManualPageSelection;
  },
  allPagesSelectedWithBatchSplitting(state): boolean {
    return state.allPagesSelectedWithBatchSplitting;
  },
  getApplyReferencesToNextDocumentEnabled(state): boolean {
    return state.applyReferencesToNextDocumentEnabled;
  }
};
